/**
 * LegalEntitiesPanel — which units are separately registered bodies.
 *
 * The hierarchy is one tree; in law it is several. Marking a unit here is what
 * tells the transfer side that money moving between two of them left a legal
 * entity and cannot simply be struck out of group totals.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Landmark, ShieldCheck, Loader2, Pencil } from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const detail = Array.isArray(err.detail)
      ? err.detail.map((d) => d.msg || String(d)).join('; ')
      : err.detail;
    throw new Error(detail || `Request failed (${res.status})`);
  }
  return res.json();
};

const FORM_LABELS = {
  charity: 'Registered charity',
  company: 'Company',
  trust: 'Trust',
  association: 'Association',
  other: 'Other',
};

export const LegalEntitiesPanel = () => {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['legal-entities'],
    queryFn: () => api('/admin/org/legal-entities'),
  });
  const { data: unitsData } = useQuery({
    queryKey: ['org-units-for-entities'],
    queryFn: () => api('/admin/org/units').catch(() => []),
  });

  const units = Array.isArray(unitsData) ? unitsData : (unitsData?.units || []);
  const entities = data?.entities || [];

  const save = useMutation({
    mutationFn: ({ unitId, values }) =>
      api(`/admin/org/units/${unitId}/legal-entity`, {
        method: 'PUT', body: JSON.stringify(values),
      }),
    onSuccess: () => {
      toast.success('Registration saved');
      qc.invalidateQueries({ queryKey: ['legal-entities'] });
      setEditing(null);
    },
    onError: (e) => toast.error(e.message),
  });

  return (
    <Card data-testid="legal-entities-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="flex items-center gap-2">
            <Landmark className="h-5 w-5 text-amber-600" /> Registered bodies
          </CardTitle>
          <CardDescription>
            Units that are charities or companies in their own right. A transfer between
            two of them leaves a legal entity, so it is reported for a decision instead of
            being eliminated from group totals.
          </CardDescription>
        </div>
        <Button
          variant="outline"
          onClick={() => setEditing({ unit_id: '', is_legal_entity: true })}
          data-testid="add-legal-entity-btn"
        >
          Mark a unit
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <div className="py-10 text-center text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
          </div>
        )}
        {!isLoading && entities.length === 0 && (
          <p className="py-8 text-center text-sm text-slate-500" data-testid="no-legal-entities">
            No unit is recorded as separately registered yet, so every transfer is treated as
            money moved inside one body.
          </p>
        )}
        <div className="space-y-2">
          {entities.map((e) => (
            <div
              key={e.unit_id}
              className="flex items-center justify-between gap-3 border rounded-lg px-3 py-2"
              data-testid={`legal-entity-row-${e.unit_id}`}
            >
              <div className="min-w-0">
                <p className="font-medium truncate flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                  {e.legal_name}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {e.unit_name}
                  {e.registration_number ? ` · No. ${e.registration_number}` : ''}
                  {e.registered_country ? ` · ${e.registered_country}` : ''}
                  {e.regulator ? ` · ${e.regulator}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {e.entity_form && (
                  <Badge variant="outline">{FORM_LABELS[e.entity_form] || e.entity_form}</Badge>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing({ ...e })}
                  data-testid={`edit-legal-entity-${e.unit_id}`}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>

      <EntityDialog
        value={editing}
        units={units}
        onClose={() => setEditing(null)}
        busy={save.isPending}
        onSave={(values) => save.mutate({ unitId: values.unit_id, values })}
      />
    </Card>
  );
};

function EntityDialog({ value, units, onClose, onSave, busy }) {
  const [form, setForm] = useState({});
  React.useEffect(() => {
    setForm(value ? {
      unit_id: value.unit_id || '',
      is_legal_entity: value.is_legal_entity !== false,
      legal_name: value.legal_name || '',
      registration_number: value.registration_number || '',
      registered_country: value.registered_country || '',
      tax_number: value.tax_number || '',
      regulator: value.regulator || '',
      entity_form: value.entity_form || 'charity',
    } : {});
  }, [value]);

  const existing = Boolean(value?.unit_id);

  return (
    <Dialog open={Boolean(value)} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="legal-entity-dialog">
        <DialogHeader>
          <DialogTitle>{existing ? 'Registration details' : 'Mark a unit as registered'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[65vh] overflow-auto pr-1">
          {!existing && (
            <div>
              <Label>Unit</Label>
              <Select value={form.unit_id || ''} onValueChange={(v) => setForm({ ...form, unit_id: v })}>
                <SelectTrigger data-testid="entity-unit-select"><SelectValue placeholder="Select a unit" /></SelectTrigger>
                <SelectContent>
                  {units.map((u) => (
                    <SelectItem key={u.id || u._id} value={u.id || u._id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center justify-between border rounded-md px-3 py-2">
            <div>
              <Label className="text-sm">Separately registered</Label>
              <p className="text-xs text-slate-500">Turn off if this unit is a branch of the one above it.</p>
            </div>
            <Switch
              checked={Boolean(form.is_legal_entity)}
              onCheckedChange={(v) => setForm({ ...form, is_legal_entity: v })}
              data-testid="entity-registered-switch"
            />
          </div>
          <div>
            <Label>Registered name</Label>
            <Input value={form.legal_name || ''} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} data-testid="entity-legal-name-input" placeholder="As it appears on the register" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Registration number</Label>
              <Input value={form.registration_number || ''} onChange={(e) => setForm({ ...form, registration_number: e.target.value })} data-testid="entity-registration-input" />
            </div>
            <div>
              <Label>Country</Label>
              <Input value={form.registered_country || ''} onChange={(e) => setForm({ ...form, registered_country: e.target.value })} data-testid="entity-country-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Form</Label>
              <Select value={form.entity_form || 'charity'} onValueChange={(v) => setForm({ ...form, entity_form: v })}>
                <SelectTrigger data-testid="entity-form-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(FORM_LABELS).map(([k, label]) => (
                    <SelectItem key={k} value={k}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Regulator</Label>
              <Input value={form.regulator || ''} onChange={(e) => setForm({ ...form, regulator: e.target.value })} data-testid="entity-regulator-input" placeholder="e.g. Charity Commission" />
            </div>
          </div>
          <div>
            <Label>Tax number (optional)</Label>
            <Input value={form.tax_number || ''} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} data-testid="entity-tax-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button
            onClick={() => {
              if (!form.unit_id) { toast.error('Pick the unit first'); return; }
              onSave(form);
            }}
            disabled={busy}
            data-testid="save-legal-entity-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default LegalEntitiesPanel;
